import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Play, Mic, Calendar, GraduationCap, Megaphone, FileText, Award } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import LanguageToggle from "./LanguageToggle";
import { initCalEmbed, calButtonProps } from "@/hooks/use-cal";

const Navbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    initCalEmbed();
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    if (activeDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeDropdown]);

  const navLinks = [{ name: t("nav.home"), href: "/" }];

  const serviceLinks = [
    { name: t("nav.svc.reels.name"), href: "/reels", icon: Play, description: t("nav.svc.reels.desc") },
    { name: t("nav.svc.podcasts.name"), href: "/podcasts", icon: Mic, description: t("nav.svc.podcasts.desc") },
    { name: t("nav.svc.events.name"), href: "/evenements", icon: Calendar, description: t("nav.svc.events.desc") },
    { name: t("nav.svc.formations.name"), href: "/formations", icon: GraduationCap, description: t("nav.svc.formations.desc") },
    { name: t("nav.svc.ads.name"), href: "/publicites", icon: Megaphone, description: t("nav.svc.ads.desc") },
  ];

  const resourceLinks = [
    { name: t("nav.res.cases.name"), href: "/etudes-de-cas", icon: Award, description: t("nav.res.cases.desc") },
    { name: t("nav.res.articles.name"), href: "/articles", icon: FileText, description: t("nav.res.articles.desc") },
    { name: t("nav.res.portfolio.name"), href: "/portfolio", icon: Play, description: t("nav.res.portfolio.desc") },
  ];

  const toggleDropdown = (dropdown: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const toggleMobileDropdown = (dropdown: string) => {
    setActiveMobileDropdown(activeMobileDropdown === dropdown ? null : dropdown);
  };

  const DropdownMenu = ({ items, isOpen, id }: { items: typeof serviceLinks; isOpen: boolean; id: string }) => (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          id={id}
          role="menu"
          aria-orientation="vertical"
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="absolute top-full mt-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-2xl p-3 min-w-[280px] shadow-xl z-50"
        >
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-card border-l border-t border-border rotate-45" />
          <div className="relative z-10">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  role="menuitem"
                  onClick={() => setActiveDropdown(null)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card ${
                    location.pathname === item.href ? "bg-primary/10 text-primary" : "hover:bg-secondary text-foreground"
                  }`}
                >
                  <div className={`p-2 rounded-lg transition-colors group-hover:scale-110 ${
                    location.pathname === item.href ? "bg-primary/20" : "bg-secondary group-hover:bg-primary/10"
                  }`}>
                    <Icon className="w-4 h-4" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{item.name}</div>
                    <div className="text-xs text-muted-foreground">{item.description}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 pt-4 px-4"
    >
      <div className={`container mx-auto flex items-center justify-between rounded-full border transition-all duration-500 pl-6 pr-3 py-2 ${
        isScrolled ? "bg-background/80 backdrop-blur-xl border-border/70 shadow-soft" : "bg-background/40 backdrop-blur-md border-border/40"
      }`}>
        <Logo />

        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className={`text-sm font-medium transition-colors duration-300 ${
                location.pathname === link.href ? "text-foreground border-b-2 border-primary pb-1" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.name}
            </Link>
          ))}

          <div className="relative">
            <button
              onClick={(e) => toggleDropdown('services', e)}
              aria-expanded={activeDropdown === 'services'}
              aria-haspopup="menu"
              aria-controls="services-menu"
              className={`flex items-center gap-1 transition-colors duration-300 text-sm font-medium rounded-lg px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                activeDropdown === 'services' || serviceLinks.some(s => location.pathname === s.href) ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("nav.services")}
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'services' ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            <DropdownMenu items={serviceLinks} isOpen={activeDropdown === 'services'} id="services-menu" />
          </div>

          <div className="relative">
            <button
              onClick={(e) => toggleDropdown('resources', e)}
              aria-expanded={activeDropdown === 'resources'}
              aria-haspopup="menu"
              aria-controls="resources-menu"
              className={`flex items-center gap-1 transition-colors duration-300 text-sm font-medium rounded-lg px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                activeDropdown === 'resources' || resourceLinks.some(r => location.pathname === r.href) ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("nav.resources")}
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'resources' ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            <DropdownMenu items={resourceLinks} isOpen={activeDropdown === 'resources'} id="resources-menu" />
          </div>

          <Link
            to="/accompagnement"
            className={`text-sm font-medium transition-colors duration-300 ${
              location.pathname === "/accompagnement" ? "text-foreground border-b-2 border-primary pb-1" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("nav.accompagnement")}
          </Link>
        </div>

        <div className="hidden lg:flex items-center gap-2">
          <LanguageToggle />
          <Link
            to="/plan-de-match"
            className="group relative flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 pl-3 pr-4 py-2 text-sm font-medium text-primary transition-all duration-300 hover:bg-primary/20 hover:border-primary/70"
          >
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
            <span className="whitespace-nowrap">Plan de match</span>
          </Link>
          <Button variant="hero" size="default" {...calButtonProps}>
            {t("nav.bookCall")}
          </Button>
        </div>


        <div className="lg:hidden flex items-center gap-2">
          <LanguageToggle />
          <button
            className="text-foreground p-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-colors hover:bg-secondary"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
          >
            {isMobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-card mt-4 mx-4 rounded-2xl overflow-hidden border border-border shadow-xl"
          >
            <div className="p-6 flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`py-3 px-4 rounded-xl transition-colors ${
                    location.pathname === link.href ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}

              <div>
                <button
                  onClick={() => toggleMobileDropdown('services')}
                  className="flex items-center justify-between w-full py-3 px-4 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                >
                  <span>{t("nav.services")}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === 'services' ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {activeMobileDropdown === 'services' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="pl-4 py-2 space-y-1">
                        {serviceLinks.map((service) => {
                          const Icon = service.icon;
                          return (
                            <Link
                              key={service.name}
                              to={service.href}
                              onClick={() => { setIsMobileMenuOpen(false); setActiveMobileDropdown(null); }}
                              className={`flex items-center gap-3 py-2 px-4 rounded-xl transition-colors ${
                                location.pathname === service.href ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              <span className="text-sm">{service.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div>
                <button
                  onClick={() => toggleMobileDropdown('resources')}
                  className="flex items-center justify-between w-full py-3 px-4 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                >
                  <span>{t("nav.resources")}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === 'resources' ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {activeMobileDropdown === 'resources' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="pl-4 py-2 space-y-1">
                        {resourceLinks.map((resource) => {
                          const Icon = resource.icon;
                          return (
                            <Link
                              key={resource.name}
                              to={resource.href}
                              onClick={() => { setIsMobileMenuOpen(false); setActiveMobileDropdown(null); }}
                              className={`flex items-center gap-3 py-2 px-4 rounded-xl transition-colors ${
                                location.pathname === resource.href ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              <span className="text-sm">{resource.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/accompagnement"
                className={`py-3 px-4 rounded-xl transition-colors ${
                  location.pathname === "/accompagnement" ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("nav.accompagnement")}
              </Link>

              <div className="pt-4 mt-2 border-t border-border flex flex-col gap-2">
                <Link
                  to="/plan-de-match"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full rounded-xl border border-primary/40 bg-primary/10 py-3 text-sm font-medium text-primary"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                  </span>
                  Plan de match
                </Link>
                <Button variant="hero" size="lg" className="w-full" onClick={() => setIsMobileMenuOpen(false)} {...calButtonProps}>
                  {t("nav.bookCall")}
                </Button>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
